import {
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import moment from 'moment';

import { fetchPrograms } from 'actions/programs';
import { showModal } from 'actions/modal';
import { requestFetchExerciseById } from 'actions/exercise';
import { requestFetchAchievements } from 'actions/achievements';
import { AchievementType, StoreType } from 'reducers/types';
import { EASE, QUALITY, pictograms } from 'constants/ponder';
import {
  ACHIEVEMENT_ICON_STATES,
  getAchievementIconComponent,
  InlineAchievementIcon,
} from 'components/Achievements';
import api from 'utils/api';
import imageUrlGenerator from 'utils/imageUrlGenerator';
import sortPrograms from 'utils/sortPrograms';

import programsIcon from 'components/Header/ic_programs.svg';
import scheduleIcon from 'components/Header/ic_schedule.svg';
import activityIcon from 'components/Header/ic_community.svg';
import profileIcon from 'components/Header/ic_profile.svg';

import {
  Overlay,
  Panel,
  SearchRow,
  SearchInput,
  Kbd,
  Results,
  GroupLabel,
  ResultButton,
  Thumb,
  IconTile,
  AwardTile,
  ItemText,
  ItemTitle,
  ItemSubtitle,
  ItemMeta,
  MetaCheck,
  MetaPictogram,
  MetaDivider,
  TypeBadge,
  StateNote,
  EmptyState,
} from './styles';

type PaletteItemType = 'page' | 'program' | 'session' | 'exercise' | 'award' | 'action';

interface PaletteItem {
  key: string;
  type: PaletteItemType;
  title: string;
  subtitle?: string;
  picture?: string;
  url?: string;
  exerciseId?: number;
  sessionId?: number;
  achievementId?: number;
}

interface SessionCompletion {
  completedAt: string;
  ease?: number;
  quality?: number;
  timeLength?: number;
}

interface PaletteGroup {
  label: string;
  items: PaletteItem[];
  loading?: boolean;
}

const TYPE_LABELS: Record<PaletteItemType, string> = {
  page: 'Page',
  program: 'Program',
  session: 'Session',
  exercise: 'Exercise',
  award: 'Award',
  action: 'Action',
};

const LOG_EXTERNAL_WORKOUT_KEY = 'action-log-external-workout';
const START_NEXT_SESSION_KEY = 'action-start-next-session';

const ACTIONS: PaletteItem[] = [
  {
    key: LOG_EXTERNAL_WORKOUT_KEY,
    type: 'action',
    title: 'Log a workout',
    subtitle: 'Capture a workout you did outside GMB',
  },
];

const PAGES: PaletteItem[] = [
  {
    key: 'page-programs',
    type: 'page',
    title: 'Programs',
    subtitle: 'All your training programs',
    url: '/programs',
  },
  {
    key: 'page-schedule',
    type: 'page',
    title: 'Schedule',
    subtitle: 'Your weekly training schedule',
    url: '/schedule',
  },
  {
    key: 'page-activity',
    type: 'page',
    title: 'Activity',
    subtitle: 'Your training history',
    url: '/activity',
  },
  {
    key: 'page-profile',
    type: 'page',
    title: 'Profile',
    subtitle: 'Your account and settings',
    url: '/profile',
  },
];

// Icons are resolved at render time (not persisted in recents) because their
// hashed asset URLs change between builds.
const PAGE_ICONS: Record<string, string> = {
  'page-programs': programsIcon,
  'page-schedule': scheduleIcon,
  'page-activity': activityIcon,
  'page-profile': profileIcon,
};

const RECENTS_STORAGE_KEY = 'gmbCommandPaletteRecents';
const MAX_RECENTS = 6;
const MAX_PROGRAM_RESULTS = 5;
const MAX_SESSION_RESULTS = 6;
const MAX_EXERCISE_RESULTS = 8;
const MAX_AWARD_RESULTS = 4;
const COMPLETED_ACTIVITIES_LIMIT = 200;
const EXERCISE_SEARCH_DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

const ponderPictogram = (options: { value: number; icon: string }[], value?: number) => {
  const icon = value ? options.find((option) => option.value === value)?.icon : undefined;
  return icon ? { src: pictograms[icon], name: icon.replace('_', ' ') } : null;
};

const readRecents = (): PaletteItem[] => {
  try {
    const raw = window.localStorage.getItem(RECENTS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const saveRecent = (item: PaletteItem) => {
  try {
    const next = [item, ...readRecents().filter((recent) => recent.key !== item.key)].slice(
      0,
      MAX_RECENTS,
    );
    window.localStorage.setItem(RECENTS_STORAGE_KEY, JSON.stringify(next));
  } catch (error) {
    // localStorage unavailable — recents are best-effort
  }
};

const AwardIcon = ({ achievement }: { achievement?: AchievementType }) => {
  if (achievement?.icon) {
    return <InlineAchievementIcon className="" html={achievement.icon} />;
  }

  const IconComponent = achievement
    ? getAchievementIconComponent(achievement, ACHIEVEMENT_ICON_STATES.earned)
    : null;

  if (IconComponent) {
    return <IconComponent />;
  }

  return <span aria-hidden="true">🏆</span>;
};

const CommandPalette = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [recents, setRecents] = useState<PaletteItem[]>([]);
  const [exerciseResults, setExerciseResults] = useState<PaletteItem[]>([]);
  const [exercisesLoading, setExercisesLoading] = useState(false);
  const [completions, setCompletions] = useState<Record<number, SessionCompletion>>({});

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const latestExerciseQuery = useRef('');

  const dispatch = useDispatch();
  const router = useRouter();
  const programs = useSelector((state: StoreType) => state.programs.all);
  const programsLoading = useSelector((state: StoreType) => state.programs.loading);
  const achievements = useSelector((state: StoreType) => state.achievements.all);

  const close = useCallback(() => {
    setIsOpen(false);
    setQuery('');
    setExerciseResults([]);
    setExercisesLoading(false);
    setActiveIndex(0);
  }, []);

  // Cmd+K (macOS) / Ctrl+K toggles the palette; Esc closes it.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        if (isOpen) {
          close();
        } else {
          setIsOpen(true);
        }
      } else if (event.key === 'Escape' && isOpen) {
        close();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, close]);

  // While open: focus the input, load recents, and lock body scroll.
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    setRecents(readRecents());
    inputRef.current?.focus();

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  // Programs (with their sessions) back the client-side results.
  useEffect(() => {
    if (isOpen && !programs.length && !programsLoading) {
      dispatch(fetchPrograms());
    }
  }, [isOpen, programs.length, programsLoading, dispatch]);

  // Earned awards are searchable and open the achievements modal.
  useEffect(() => {
    if (isOpen && !achievements) {
      dispatch(requestFetchAchievements());
    }
  }, [isOpen, achievements, dispatch]);

  // The activity feed (newest first) tells us which sessions were completed,
  // with the ponder answers and minutes shown on completed session results.
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    let cancelled = false;
    api()
      .get('/activities/', { params: { limit: COMPLETED_ACTIVITIES_LIMIT, offset: 0 } })
      .then(({ data }) => {
        if (cancelled) {
          return;
        }

        const map: Record<number, SessionCompletion> = {};
        (data?.all || data?.items || []).forEach(
          (activity: {
            type: string;
            completedAt?: string;
            timeLength?: number;
            ease?: number;
            quality?: number;
            session?: { id: number };
          }) => {
            const sessionId = activity.session?.id;
            if (activity.type !== 'workout' || !sessionId || !activity.completedAt) {
              return;
            }
            if (!map[sessionId]) {
              map[sessionId] = {
                completedAt: activity.completedAt,
                ease: activity.ease,
                quality: activity.quality,
                timeLength: activity.timeLength,
              };
            }
          },
        );
        setCompletions(map);
      })
      .catch(() => {
        // Completion badges are best-effort decoration
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    const handleRouteChange = () => close();

    router.events.on('routeChangeStart', handleRouteChange);
    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [router.events, close]);

  // Exercises live behind a search endpoint, so query them debounced.
  useEffect(() => {
    const trimmed = query.trim();

    if (!isOpen || trimmed.length < MIN_QUERY_LENGTH) {
      latestExerciseQuery.current = '';
      setExerciseResults([]);
      setExercisesLoading(false);
      return undefined;
    }

    latestExerciseQuery.current = trimmed;
    setExercisesLoading(true);

    const timer = setTimeout(() => {
      api()
        .get('/exercise', { params: { q: trimmed, limit: MAX_EXERCISE_RESULTS } })
        .then(({ data }) => {
          if (latestExerciseQuery.current !== trimmed) {
            return;
          }

          const items: PaletteItem[] = (data?.items || []).map(
            (exercise: {
              id: number;
              name: string;
              picture: string;
              description: string;
              sessionInfo?: { sessionName?: string; programName?: string }[];
            }) => {
              const sessionInfo = exercise.sessionInfo?.find((info) => info.sessionName);
              const subtitle = sessionInfo
                ? [sessionInfo.programName, sessionInfo.sessionName].filter(Boolean).join(' · ')
                : exercise.description;

              return {
                key: `exercise-${exercise.id}`,
                type: 'exercise' as const,
                title: exercise.name,
                subtitle,
                picture: exercise.picture,
                exerciseId: exercise.id,
              };
            },
          );

          setExerciseResults(items);
          setExercisesLoading(false);
        })
        .catch(() => {
          if (latestExerciseQuery.current !== trimmed) {
            return;
          }
          setExerciseResults([]);
          setExercisesLoading(false);
        });
    }, EXERCISE_SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query, isOpen]);

  // The "next up" session, picked the same way as the dashboard widget:
  // the top program after sortPrograms that still has a next session.
  const availableActions = useMemo((): PaletteItem[] => {
    const items = [...ACTIONS];
    const featured = programs.length ? sortPrograms([...programs])[0] : null;

    if (featured?.id && featured.nextSession?.id) {
      items.unshift({
        key: START_NEXT_SESSION_KEY,
        type: 'action',
        title: 'Start next session',
        subtitle: [featured.name, featured.nextSession.name].filter(Boolean).join(' · '),
        picture: featured.nextSession.picture,
        url: `/programs/${featured.id}/sessions/${featured.nextSession.id}/start${
          featured.nextTrackSessionId ? `?trackSessionId=${featured.nextTrackSessionId}` : ''
        }`,
      });
    }

    return items;
  }, [programs]);

  const groups = useMemo((): PaletteGroup[] => {
    const trimmed = query.trim().toLowerCase();

    if (!trimmed) {
      const emptyStateGroups: PaletteGroup[] = [];
      if (recents.length) {
        emptyStateGroups.push({ label: 'Recent', items: recents });
      }
      emptyStateGroups.push({ label: 'Actions', items: availableActions });
      emptyStateGroups.push({ label: 'Go to', items: PAGES });
      return emptyStateGroups;
    }

    const matches = (...values: (string | null | undefined)[]) =>
      values.some((value) => value && value.toLowerCase().includes(trimmed));

    const pageItems = PAGES.filter((page) => matches(page.title));

    const actionItems = availableActions.filter((action) => matches(action.title, action.subtitle));

    const programItems: PaletteItem[] = programs
      .filter((program) => matches(program.name, program.description))
      .slice(0, MAX_PROGRAM_RESULTS)
      .map((program) => ({
        key: `program-${program.id}`,
        type: 'program' as const,
        title: program.name,
        subtitle: program.description,
        picture: program.picture,
        url: `/programs/${program.id}`,
      }));

    const sessionItems: PaletteItem[] = [];
    const seenSessionIds = new Set<number>();
    programs.forEach((program) => {
      (program.programTrack?.programTrackSessions || []).forEach((trackSession) => {
        const { session } = trackSession;
        if (
          !session ||
          // Basic (low intensity) sessions are only reachable via the
          // "lighter session" request in the SessionStart flow, so keep them
          // out of quick navigation.
          session.isBasic ||
          session.challengeOrder != null ||
          seenSessionIds.has(session.id) ||
          sessionItems.length >= MAX_SESSION_RESULTS ||
          !matches(session.name, session.description)
        ) {
          return;
        }

        seenSessionIds.add(session.id);
        sessionItems.push({
          key: `session-${session.id}`,
          type: 'session',
          title: session.name,
          subtitle: program.name,
          picture: session.picture,
          url: `/programs/${program.id}/sessions/${session.id}?trackSessionId=${trackSession.id}`,
          sessionId: session.id,
        });
      });
    });

    const awardItems: PaletteItem[] = (achievements || [])
      .filter((achievement) => achievement.earnedAt && matches(achievement.name))
      .slice(0, MAX_AWARD_RESULTS)
      .map((achievement) => ({
        key: `award-${achievement.id}`,
        type: 'award' as const,
        title: achievement.name,
        subtitle: `Earned ${moment(achievement.earnedAt).format('MMM D, YYYY')}`,
        achievementId: achievement.id,
      }));

    const searchGroups: PaletteGroup[] = [];
    if (actionItems.length) {
      searchGroups.push({ label: 'Actions', items: actionItems });
    }
    if (pageItems.length) {
      searchGroups.push({ label: 'Pages', items: pageItems });
    }
    if (programItems.length) {
      searchGroups.push({ label: 'Programs', items: programItems });
    }
    if (sessionItems.length) {
      searchGroups.push({ label: 'Sessions', items: sessionItems });
    }
    if (awardItems.length) {
      searchGroups.push({ label: 'Awards', items: awardItems });
    }
    if (exerciseResults.length || exercisesLoading) {
      searchGroups.push({ label: 'Exercises', items: exerciseResults, loading: exercisesLoading });
    }
    return searchGroups;
  }, [query, recents, programs, achievements, availableActions, exerciseResults, exercisesLoading]);

  const flatItems = useMemo(() => groups.flatMap((group) => group.items), [groups]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query, flatItems.length]);

  useEffect(() => {
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const onSelect = useCallback(
    (item: PaletteItem) => {
      // Actions always sit in their own group and the next-session URL goes
      // stale as the user progresses, so they're not persisted as recents.
      if (item.type !== 'action') {
        saveRecent(item);
      }

      if (item.type === 'action' && item.key === LOG_EXTERNAL_WORKOUT_KEY) {
        close();
        dispatch(showModal('LOG_EXTERNAL_WORKOUT'));
      } else if (item.type === 'exercise' && item.exerciseId) {
        close();
        dispatch(requestFetchExerciseById({ exerciseId: item.exerciseId }));
        dispatch(showModal('EXERCISE_DETAILS', { showSessionLink: true }));
      } else if (item.type === 'award' && item.achievementId) {
        close();
        dispatch(showModal('ACHIEVEMENTS', { focusAchievementId: item.achievementId }));
      } else if (item.url) {
        close();
        router.push(item.url);
      }
    },
    [close, dispatch, router],
  );

  const onInputKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLInputElement>) => {
      if (!flatItems.length) {
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveIndex((index) => (index + 1) % flatItems.length);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIndex((index) => (index - 1 + flatItems.length) % flatItems.length);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        const item = flatItems[activeIndex];
        if (item) {
          onSelect(item);
        }
      }
    },
    [flatItems, activeIndex, onSelect],
  );

  const onOverlayMouseDown = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget) {
        close();
      }
    },
    [close],
  );

  const renderItemTile = (item: PaletteItem) => {
    if (item.type === 'page') {
      return (
        <IconTile>
          <img src={PAGE_ICONS[item.key]} alt="" />
        </IconTile>
      );
    }

    if (item.type === 'action') {
      if (item.picture) {
        return <Thumb src={imageUrlGenerator(item.picture, '96x96', '96x96')} alt="" />;
      }

      return (
        <IconTile>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M12 5V19M5 12H19"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </IconTile>
      );
    }

    if (item.type === 'award') {
      const achievement = achievements?.find(({ id }) => id === item.achievementId);
      return (
        <AwardTile>
          <AwardIcon achievement={achievement} />
        </AwardTile>
      );
    }

    return <Thumb src={imageUrlGenerator(item.picture, '96x96', '96x96')} alt="" />;
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Overlay onMouseDown={onOverlayMouseDown}>
      <Panel role="dialog" aria-modal="true" aria-label="Command palette">
        <SearchRow>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
            <path
              d="M16.5 16.5L21 21"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          <SearchInput
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder="Search programs, sessions, exercises…"
            aria-label="Search programs, sessions, exercises"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />
          <Kbd>esc</Kbd>
        </SearchRow>
        <Results ref={listRef}>
          {groups.map((group) => (
            <div key={group.label}>
              <GroupLabel>{group.label}</GroupLabel>
              {group.items.map((item) => {
                const index = flatItems.indexOf(item);
                const isActive = index === activeIndex;
                const completion =
                  item.type === 'session' && item.sessionId
                    ? completions[item.sessionId]
                    : undefined;
                const easeIcon = completion ? ponderPictogram(EASE, completion.ease) : null;
                const qualityIcon = completion
                  ? ponderPictogram(QUALITY, completion.quality)
                  : null;

                return (
                  <ResultButton
                    key={item.key}
                    type="button"
                    $active={isActive}
                    data-active={isActive}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => onSelect(item)}
                  >
                    {renderItemTile(item)}
                    <ItemText>
                      <ItemTitle>{item.title}</ItemTitle>
                      {item.subtitle && <ItemSubtitle>{item.subtitle}</ItemSubtitle>}
                      {completion && (
                        <ItemMeta>
                          <MetaCheck>✓</MetaCheck>
                          <span>{moment(completion.completedAt).format('MMM D, YYYY')}</span>
                          {easeIcon && (
                            <MetaPictogram
                              src={easeIcon.src}
                              alt={`Ease: ${easeIcon.name}`}
                              title={`Ease: ${easeIcon.name}`}
                            />
                          )}
                          {qualityIcon && (
                            <MetaPictogram
                              src={qualityIcon.src}
                              alt={`Quality: ${qualityIcon.name}`}
                              title={`Quality: ${qualityIcon.name}`}
                            />
                          )}
                          {typeof completion.timeLength === 'number' && (
                            <>
                              <MetaDivider>·</MetaDivider>
                              <span>{completion.timeLength} min</span>
                            </>
                          )}
                        </ItemMeta>
                      )}
                    </ItemText>
                    <TypeBadge>{TYPE_LABELS[item.type]}</TypeBadge>
                  </ResultButton>
                );
              })}
              {group.loading && !group.items.length && <StateNote>Searching…</StateNote>}
            </div>
          ))}
          {!flatItems.length && !exercisesLoading && query.trim() && (
            <EmptyState>No results for “{query.trim()}”</EmptyState>
          )}
        </Results>
      </Panel>
    </Overlay>
  );
};

export default CommandPalette;
